/* eslint-disable max-lines */
import TwoFactorAuthHandler from "./twoFactorAuthUtils";

const MODAL_SELECTOR = "#verification-code-modal";
const VERIFICATION_CODE_LENGTH = 6;

/**
 * Manages two-factor authentication modal display and interactions
 * Coordinates between the UI modal and the authentication handler
 */
class TwoFactorModalManager {
  /**
   * Creates a new TwoFactorModalManager instance
   * @param {Object} context - The context object containing required dependencies
   * @param {string} context.addressId - The ID of the address being managed
   * @param {Object} context.props - Component props
   * @param {TwoFactorAuthHandler} context.authHandler - Instance of TwoFactorAuthHandler
   * @param {Object} context.errorMessageRef - Ref object for error messages
   * @param {Object} context.isSubmittingRef - Ref object for tracking submission state
   */
  constructor(context) {
    this.addressId = context.addressId;
    this.props = context.props;
    this.authHandler = context.authHandler;
    this.errorMessageRef = context.errorMessageRef;
    this.isSubmittingRef = context.isSubmittingRef;
  }

  /**
   * Updates modal title and subtitle based on save state
   * @param {HTMLElement} modal - The modal element
   */
  static updateModalContent(modal) {
    const title = modal.querySelector(".modal-title");
    const subtitle = modal.querySelector("#verification-subtitle");
    const { preSave } = modal.dataset;

    if (title) {
      title.textContent = "Verify Your Address";
    }

    if (subtitle) {
      if (preSave === "true") {
        subtitle.innerHTML = `To complete adding this address, please verify it by entering the 6-digit code sent to your email.<br>
                             Your address will be saved after verification.`;
      } else {
        subtitle.innerHTML = `Your address has been saved successfully.<br>
                             To complete the process and use this address for shipping, please verify it by entering the 6-digit code sent to your email.`;
      }
    }
  }

  /**
   * Sets up input event handlers for the verification code input
   * @param {HTMLElement} modal - The modal element
   */
  setupCodeInput(modal) {
    const codeInput = modal.querySelector("#verification-code-input");

    if (!codeInput) return;

    const newCodeInput = codeInput.cloneNode(true);
    codeInput.parentNode.replaceChild(newCodeInput, codeInput);

    newCodeInput.value = "";
    newCodeInput.focus();

    const handleInput = event => {
      const input = event.target;
      input.value = input.value.replace(/\D/g, "");

      if (input.value.length === VERIFICATION_CODE_LENGTH) {
        this.authHandler.handleVerification(modal, input.value);
      }

      const errorDiv = modal.querySelector("#verification-error");
      if (errorDiv && errorDiv.style.display !== "none") {
        errorDiv.style.display = "none";
      }
    };

    newCodeInput.addEventListener("input", handleInput);
  }

  /**
   * Sets up button event handlers for verify and resend buttons
   * @param {HTMLElement} modal - The modal element
   */
  setupButtons(modal) {
    const verifyBtn = modal.querySelector("#verify-code-btn");
    const resendBtn = modal.querySelector("#resend-code-btn");

    if (verifyBtn) {
      verifyBtn.onclick = () => {
        const currentCodeInput = modal.querySelector("#verification-code-input");
        const code = currentCodeInput?.value || "";
        this.authHandler.handleVerification(modal, code);
      };
    }

    if (resendBtn) {
      resendBtn.onclick = e => {
        e.preventDefault();
        if (!resendBtn.disabled) {
          TwoFactorAuthHandler.sendVerificationCode(modal);
        }
      };
    }
  }

  /**
   * Initializes modal content and event handlers
   * Resets form, updates content, and sends initial verification code
   * @param {HTMLElement} modal - The modal element
   */
  initializeModalContent(modal) {
    TwoFactorAuthHandler.resetModalForm(modal);
    TwoFactorModalManager.updateModalContent(modal);
    this.setupCodeInput(modal);
    this.setupButtons(modal);
    TwoFactorAuthHandler.sendVerificationCode(modal);
  }

  /** Adds custom class to modal backdrop */
  static addBackdropClass() {
    setTimeout(() => {
      const $backdrop = document.querySelector(".modal-backdrop");
      if ($backdrop) {
        $backdrop.classList.add("verification-modal-backdrop");
      }
    }, 0);
  }

  /** Shows modal using Bootstrap 5 */
  static showBootstrap5Modal(modal) {
    const bsModal = new window.bootstrap.Modal(modal);
    bsModal.show();
    TwoFactorModalManager.addBackdropClass();
  }

  /** Shows modal using Bootstrap 4/jQuery */
  static showBootstrap4Modal(modal, handleModalClose) {
    window.$(modal).modal("show");
    window.$(modal).off("hidden.bs.modal").on("hidden.bs.modal", handleModalClose);
    TwoFactorModalManager.addBackdropClass();
  }

  /**
   * Shows modal using fallback method for browsers without Bootstrap
   * @param {HTMLElement} modal - The modal element
   * @param {Function} handleModalClose - Callback function for modal close event
   */
  static showFallbackModal(modal, handleModalClose) {
    // eslint-disable-next-line no-param-reassign
    modal.style.display = "block";
    modal.classList.add("show");
    modal.setAttribute("aria-hidden", "false");

    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop fade show verification-modal-backdrop";
    document.body.appendChild(backdrop);
    document.body.classList.add("modal-open");

    backdrop.addEventListener("click", handleModalClose);
  }

  /**
   * Shows the modal using appropriate method based on available libraries
   * @param {HTMLElement} modal - The modal element
   * @param {Function} handleModalClose - Callback function for modal close event
   */
  static displayModal(modal, handleModalClose) {
    if (window.bootstrap && window.bootstrap.Modal) {
      TwoFactorModalManager.showBootstrap5Modal(modal);
    } else if (window.$ && window.$.fn.modal) {
      TwoFactorModalManager.showBootstrap4Modal(modal, handleModalClose);
    } else {
      TwoFactorModalManager.showFallbackModal(modal, handleModalClose);
    }
  }

  /**
   * Configures modal dataset attributes for state tracking
   * @param {HTMLElement} modal - The modal element
   */
  configureModalDataset(modal) {
    // eslint-disable-next-line no-param-reassign
    modal.dataset.postSave = "false";
    // eslint-disable-next-line no-param-reassign
    modal.dataset.preSave = "true";
    // eslint-disable-next-line no-param-reassign
    modal.dataset.addressId = this.addressId || this.props.address?.id || "new";
  }

  /**
   * Sets up modal close event handlers for Bootstrap modal events
   * @param {HTMLElement} modal - The modal element
   * @param {Function} handleModalClose - Callback function for modal close event
   */
  static setupModalCloseHandlers(modal, handleModalClose) {
    modal.removeEventListener("hidden.bs.modal", handleModalClose);
    modal.removeEventListener("hide.bs.modal", handleModalClose);

    modal.addEventListener("hidden.bs.modal", handleModalClose);
    modal.addEventListener("hide.bs.modal", handleModalClose);
  }

  /**
   * Shows the two-factor authentication modal
   * Configures modal, sets up event handlers, and displays the modal
   */
  show() {
    const modal = document.querySelector(MODAL_SELECTOR);

    if (!modal) {
      this.errorMessageRef.value = "2FA modal not found in DOM";
      return;
    }

    this.configureModalDataset(modal);

    const handleModalClose = () => {
      this.authHandler.hideModal(modal);
    };

    TwoFactorModalManager.setupModalCloseHandlers(modal, handleModalClose);

    try {
      TwoFactorModalManager.displayModal(modal, handleModalClose);
      this.initializeModalContent(modal);
    } catch (error) {
      this.errorMessageRef.value = `Failed to show modal: ${error.message}`;
    }
  }
}

export default TwoFactorModalManager;
